import React, { useState, useEffect, useCallback, useRef } from 'react';
import styled from 'styled-components';
import {
  Smartphone, Info, User, Zap, Search, Monitor,
  TrendingUp, Database, Mail, FileText,
  Settings, ChevronDown, ChevronRight, LogOut,
  Crown, GitBranch, FolderPlus, Pencil, Trash2,
  LayoutDashboard, Target, Copy, Instagram, LayoutGrid, BarChart3,
  Globe, Video, MessageCircle, Heart,
  Contact, Server,
  PanelLeftClose, PanelLeftOpen, AlertTriangle, Layers
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import { getProfilePicUrl, getLocalProfilePicUrl } from '../../config/imageUrls';
import { keyframes } from 'styled-components';

/* ═══════════════════════════════════════════════
   STYLED COMPONENTS
   ═══════════════════════════════════════════════ */

const scriptRunSpin = keyframes`
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
`;

const SidebarWrapper = styled.div`
  position: fixed;
  left: 0; top: 0;
  height: 100vh;
  display: flex;
  z-index: 1000;

  @media (max-width: 768px) {
    /* On mobile, sidebar is an overlay triggered by hamburger */
    position: fixed;
    left: ${p => p.$mobileOpen ? '0' : '-100%'};
    top: 0;
    width: 85vw;
    max-width: 320px;
    height: 100vh;
    z-index: 9999;
    transition: left 0.3s ease;
    box-shadow: ${p => p.$mobileOpen ? '4px 0 20px rgba(0,0,0,0.5)' : 'none'};
  }
`;

/* Mobile overlay backdrop */
const MobileOverlay = styled.div`
  display: none;
  @media (max-width: 768px) {
    display: ${p => p.$open ? 'block' : 'none'};
    position: fixed;
    top: 0; left: 0; right: 0; bottom: 0;
    background: rgba(0,0,0,0.5);
    z-index: 9998;
  }
`;

/* Mobile hamburger button (top-left) */
const MobileHamburger = styled.button`
  display: none;
  @media (max-width: 768px) {
    display: flex;
    position: fixed;
    top: 12px; left: 12px;
    z-index: 9997;
    width: 40px; height: 40px;
    border-radius: 10px;
    background: var(--bg);
    border: 1px solid var(--border);
    color: var(--text-primary);
    align-items: center; justify-content: center;
    cursor: pointer;
    box-shadow: 0 2px 8px rgba(0,0,0,0.2);
  }
`;

/* Mobile bottom navigation bar */
const MobileBottomNav = styled.div`
  display: none;
  @media (max-width: 768px) {
    display: flex;
    position: fixed;
    bottom: 0; left: 0; right: 0;
    height: 64px;
    background: var(--bg);
    border-top: 1px solid var(--border);
    z-index: 9996;
    justify-content: space-around;
    align-items: center;
    padding: 0 8px;
    padding-bottom: env(safe-area-inset-bottom, 0);
  }
`;

const MobileNavItem = styled.button`
  display: flex; flex-direction: column; align-items: center; gap: 2px;
  background: none; border: none; cursor: pointer;
  color: ${p => p.$active ? 'var(--primary)' : 'var(--text-muted)'};
  font-size: 10px; font-weight: ${p => p.$active ? '700' : '500'};
  padding: 4px 8px; border-radius: 8px;
  transition: all .15s;
  svg { opacity: ${p => p.$active ? '1' : '.6'}; }
  &:hover { color: var(--primary); }
`;

// ── NARROW BAR (dynamic width, collapsible) ──────────────
const NARROW_EXPANDED = 160;
const NARROW_COLLAPSED = 48;

const NarrowBar = styled.div`
  width: ${p => p.$collapsed ? NARROW_COLLAPSED : NARROW_EXPANDED}px;
  height: 100vh;
  background: var(--bg);
  border-right: 1px solid var(--border);
  display: flex; flex-direction: column;
  align-items: ${p => p.$collapsed ? 'center' : 'stretch'};
  padding: 12px 0; flex-shrink: 0;
  transition: width .25s ease;
  overflow-y: hidden; overflow-x: visible;
  position: relative; z-index: 1002;
`;

const BrandLogo = styled.div`
  width: ${p => p.$collapsed ? '32px' : '80px'};
  height: ${p => p.$collapsed ? '32px' : '80px'};
  margin-bottom: ${p => p.$collapsed ? '8px' : '12px'};
  cursor: pointer; align-self: center; flex-shrink: 0;
  transition: all .25s ease;
  img { width:100%; height:100%; object-fit:contain;
    filter: drop-shadow(0 0 8px rgba(165,145,239,.4)); transition: all .2s; }
  &:hover img { filter: drop-shadow(0 0 12px rgba(165,145,239,.6)); transform: scale(1.05); }
`;

const WorkstationBadge = styled.div`
  font-size: 8px; font-weight: 800; color: var(--primary);
  background: var(--surface); border: 1px solid var(--border);
  border-radius: 6px; padding: 2px 6px; margin-bottom: 4px;
  text-align: center; max-width: 120px; overflow: hidden;
  text-overflow: ellipsis; white-space: nowrap; cursor: pointer;
  letter-spacing: 0.5px; text-transform: uppercase;
  transition: all .15s; position: relative;
  &:hover { border-color: var(--primary); background: rgba(165,145,239,.1); }
`;

const WsDropdown = styled.div`
  position: absolute; top: 100%; left: 50%; transform: translateX(-50%);
  margin-top: 4px; z-index: 3000;
  background: var(--surface); border: 1px solid var(--border);
  border-radius: 8px; padding: 4px 0; min-width: 160px;
  box-shadow: 0 4px 20px rgba(0,0,0,.25);
`;

const WsDropItem = styled.button`
  display: flex; align-items: center; gap: 8px; width: 100%;
  padding: 7px 12px; background: ${p => p.$active ? 'rgba(165,145,239,.12)' : 'none'};
  border: none; border-left: 3px solid ${p => p.$active ? 'var(--primary)' : 'transparent'};
  color: ${p => p.$active ? 'var(--primary)' : 'var(--text-primary)'};
  font-size: 11px; font-weight: ${p => p.$active ? '700' : '500'};
  cursor: pointer; transition: all .1s; text-align: left;
  &:hover { background: var(--surface-hover); color: var(--primary); }
`;

const WsDropDot = styled.span`
  width: 7px; height: 7px; border-radius: 50%; flex-shrink: 0;
  background: ${p => p.$online ? '#22c55e' : 'var(--text-muted)'};
  box-shadow: ${p => p.$online ? '0 0 4px rgba(34,197,94,.5)' : 'none'};
`;

const WsDropCount = styled.span`
  font-size: 9px; color: var(--text-muted); margin-left: auto;
`;

const NDivider = styled.div`
  width: calc(100% - 16px); height: 1px; background: var(--border); margin: 6px auto;
`;

const StickyFilterBar = styled.div`
  position: sticky; top: 0; z-index: 5; flex-shrink: 0;
  display: flex; flex-direction: column; align-items: center;
  gap: 4px; padding: 4px 0 6px; width: 100%;
  background: var(--bg);
  ${p => p.$collapsed && 'display: none;'}
`;

const DeviceListWrap = styled.div`
  flex: 1; display: flex; flex-direction: column;
  align-items: center;
  gap: 4px; overflow-y: auto; overflow-x: visible;
  padding: ${p => p.$collapsed ? '4px 2px 6px' : '0 0 6px'};
  width: 100%;
  scrollbar-width: none; -ms-overflow-style: none;
  &::-webkit-scrollbar { width: 0; height: 0; display: none; }
`;

const GroupBubble = styled.div`
  width: calc(100% - 16px); min-height: 42px; border-radius: 10px;
  background: ${p => p.$color ? p.$color + '18' : 'rgba(165,145,239,.08)'};
  border-left: 3px solid ${p => p.$color || 'var(--primary)'};
  border-top: 1px solid ${p => p.$dropTarget ? 'var(--primary)' : 'transparent'};
  border-right: 1px solid ${p => p.$dropTarget ? 'var(--primary)' : 'transparent'};
  border-bottom: 1px solid ${p => p.$dropTarget ? 'var(--primary)' : 'transparent'};
  display: flex; align-items: center; justify-content: center; flex-direction: column;
  cursor: pointer; user-select: none; transition: all .2s;
  padding: 8px 4px; gap: 2px; position: relative;
  box-shadow: ${p => p.$dropTarget ? '0 0 10px rgba(165,145,239,.4)' : 'none'};
  &:hover { background: ${p => p.$color ? p.$color + '25' : 'rgba(165,145,239,.12)'}; }
`;

const GroupName = styled.span`
  font-size: ${p => p.$big ? '20px' : '10px'}; font-weight: ${p => p.$big ? '900' : '700'}; color: ${p => p.$big ? (p.$color || 'var(--text-primary)') : 'var(--text-primary)'};
  max-width: 130px; overflow: hidden; text-overflow: ellipsis;
  white-space: nowrap; text-align: center; line-height: 1.1;
  ${p => p.$big && `letter-spacing: 1px; text-shadow: 0 1px 4px ${(p.$color || '#6366f1')}40;`}
`;

const GroupCounts = styled.div`
  display: flex; gap: 6px; align-items: center;
`;

const CountBadge = styled.span`
  font-size: 9px; font-weight: 800; line-height: 1;
  color: ${p => p.$green ? '#22c55e' : 'var(--text-muted)'};
`;

const CollapsedPics = styled.div`
  display: flex; flex-wrap: wrap; justify-content: center;
  gap: 2px; max-width: 64px; margin-top: 3px;
`;

const MiniPic = styled.img`
  width: 18px; height: 18px; border-radius: 50%; object-fit: cover;
  border: 1.5px solid ${p => p.$online ? '#22c55e' : 'var(--border)'};
  display: block;
`;

const MiniPicFallback = styled.div`
  width: 18px; height: 18px; border-radius: 50%;
  background: var(--surface); display: flex; align-items: center; justify-content: center;
  font-size: 7px; font-weight: 700; color: var(--text-muted);
  border: 1.5px solid ${p => p.$online ? '#22c55e' : 'var(--border)'};
`;

const MiniPicWrap = styled.div`
  position: relative; width: 18px; height: 18px;
`;

const MiniRunningRing = styled.div`
  position: absolute; inset: -3px;
  border-radius: 50%; pointer-events: none;
  border: 1.5px solid transparent;
  border-top-color: var(--primary);
  border-right-color: rgba(165,145,239,.35);
  animation: ${scriptRunSpin} 1.2s linear infinite;
  filter: drop-shadow(0 0 2px rgba(165,145,239,.5));
`;

const GroupDevicesWrap = styled.div`
  display: flex; flex-direction: column; align-items: stretch; gap: 2px;
  padding: 4px 4px 6px;
  margin: -2px 4px 2px;
  border-radius: 0 0 10px 10px;
  background: ${p => p.$color ? p.$color + '0c' : 'rgba(165,145,239,.04)'};
  border-left: 2px solid ${p => p.$color ? p.$color + '30' : 'rgba(165,145,239,.15)'};
  border-right: 1px solid transparent;
  position: relative;
  overflow: visible;
  min-width: 0;
  z-index: 1001;
`;

const DropLine = styled.div`
  width: 36px; height: 2px; border-radius: 1px;
  background: ${p => p.$active ? 'var(--primary)' : 'transparent'};
  transition: background .1s; flex-shrink: 0;
`;

const GroupSep = styled.div`
  width: 40px; height: 2px; border-radius: 1px;
  background: ${p => p.$dropTarget ? 'var(--primary)' : 'var(--border)'};
  margin: 2px 0; transition: all .15s;
`;

const DeviceSlot = styled.div`
  display: flex;
  flex-direction: ${p => p.$inline ? 'row' : 'column'};
  align-items: center;
  gap: ${p => p.$inline ? '8px' : '0'};
  width: ${p => p.$inline ? '100%' : 'auto'};
  min-width: ${p => p.$inline ? 'fit-content' : 'auto'};
  padding: ${p => p.$inline ? '3px 8px' : '0'};
  border-radius: ${p => p.$inline ? '10px' : '0'};
  cursor: ${p => p.$inline ? 'pointer' : 'default'};
  background: ${p => p.$sel ? 'rgba(165,145,239,.1)' : 'transparent'};
  transition: background .12s;
  ${p => p.$inline && `&:hover { background: var(--surface-hover); }`}
`;

const DeviceSlotName = styled.span`
  font-size: 11px; font-weight: ${p => p.$sel ? '700' : '500'};
  color: ${p => p.$sel ? 'var(--primary)' : 'var(--text-secondary)'};
  white-space: nowrap;
  flex: 1; min-width: 0;
`;

const DeviceSlotIg = styled.span`
  font-size: 9px; color: var(--text-muted); font-weight: 500;
  overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
  max-width: 80px;
`;

const ContextMenu = styled.div`
  position: fixed; z-index: 2000;
  background: var(--surface); border: 1px solid var(--border);
  border-radius: 8px; padding: 4px 0; min-width: 160px;
  box-shadow: 0 4px 20px rgba(0,0,0,.15);
`;

const CtxItem = styled.button`
  display: flex; align-items: center; gap: 8px; width: 100%;
  padding: 7px 12px; background: none; border: none;
  color: ${p => p.$danger ? 'var(--error)' : 'var(--text-primary)'};
  font-size: 12px; cursor: pointer; transition: all .1s;
  &:hover { background: var(--surface-hover); }
`;

const InlineInput = styled.input`
  width: 100%; padding: 3px 6px; border-radius: 4px;
  border: 1px solid var(--primary); background: var(--input-bg);
  color: var(--text-primary); font-size: 10px; font-weight: 700;
  outline: none; text-align: center;
`;

const DeviceThumb = styled.div`
  width: ${p => p.$mini ? '36px' : '52px'}; height: ${p => p.$mini ? '36px' : '52px'}; border-radius: 50%;
  background: ${p => p.$sel ? 'var(--primary)' : p.$dragOver ? 'rgba(165,145,239,.15)' : 'var(--surface)'};
  border: ${p => p.$sel ? '3px solid var(--primary)' : `2px solid ${p.$online ? 'rgba(34,197,94,.8)' : p.$dragOver ? 'var(--primary)' : 'var(--border)'}`};
  display: flex; align-items: center; justify-content: center;
  cursor: grab; transition: all .2s; position: relative;
  color: ${p => p.$sel ? '#fff' : 'var(--text-muted)'};
  font-size: 12px; font-weight: 700;
  opacity: ${p => p.$dragging ? '.4' : '1'};
  overflow: visible; flex-shrink: 0;
  transform: ${p => p.$sel ? 'scale(1.12)' : 'scale(1)'};
  box-shadow: ${p => p.$sel ? '0 0 0 3px rgba(165,145,239,.35), 0 2px 12px rgba(165,145,239,.4)' : 'none'};
  z-index: ${p => p.$sel ? '2' : '1'};
  &:hover { border-color: ${p => p.$sel ? 'var(--primary)' : p.$online ? 'rgba(34,197,94,.7)' : 'var(--primary)'};
    background: ${p => p.$sel ? 'var(--primary)' : 'var(--surface-hover)'}; }
  &:active { cursor: grabbing; }
`;

const ThumbImg = styled.img`
  width: 100%; height: 100%; object-fit: cover; border-radius: 50%;
  pointer-events: none; user-select: none; display: block;
`;

const OnlineDot = styled.div`
  position: absolute; bottom: 1px; right: 1px;
  width: 13px; height: 13px; border-radius: 50%;
  background: ${p => p.$on ? '#22c55e' : 'var(--text-muted)'};
  border: 2px solid var(--bg);
  pointer-events: none;
  box-shadow: ${p => p.$on ? '0 0 6px rgba(34,197,94,.6)' : 'none'};
`;

const ScriptRunningRing = styled.div`
  position: absolute; inset: -4px;
  border-radius: 50%;
  pointer-events: none;
  border: 2.5px solid transparent;
  border-top-color: var(--primary);
  border-right-color: rgba(165,145,239,.4);
  animation: ${scriptRunSpin} 1.2s linear infinite;
  filter: drop-shadow(0 0 4px rgba(165,145,239,.5));
`;

const NBottom = styled.div`
  display: flex; flex-direction: column;
  align-items: ${p => p.$collapsed ? 'center' : 'stretch'};
  gap: 2px; margin-top: auto; padding: 8px ${p => p.$collapsed ? '0' : '6px'} 0;
`;

const NIconBtn = styled.button`
  height: ${p => p.$collapsed ? '36px' : '32px'};
  width: ${p => p.$collapsed ? '36px' : '100%'};
  border-radius: 8px;
  background: ${p => p.$act ? 'var(--primary)' : 'transparent'};
  border: none; cursor: pointer;
  display: flex; align-items: center;
  justify-content: ${p => p.$collapsed ? 'center' : 'flex-start'};
  gap: 10px;
  padding: ${p => p.$collapsed ? '0' : '0 10px'};
  color: ${p => p.$act ? '#fff' : 'var(--text-muted)'};
  font-size: 12px; font-weight: 500;
  white-space: nowrap; overflow: hidden;
  transition: all .15s;
  &:hover { background: ${p => p.$act ? 'var(--primary)' : 'var(--surface)'};
    color: ${p => p.$act ? '#fff' : 'var(--primary)'}; }
  svg { flex-shrink: 0; }
`;

const CollapseToggle = styled.button`
  width: 100%; height: 32px; border: none; cursor: pointer;
  display: flex; align-items: center; justify-content: center;
  gap: 6px; background: transparent; color: var(--text-muted);
  font-size: 10px; font-weight: 600; transition: all .15s;
  border-top: 1px solid var(--border); margin-top: 6px;
  &:hover { color: var(--primary); background: var(--surface-hover); }
`;

const AvatarDot = styled.div`
  width: 44px; height: 44px; border-radius: 50%; background: var(--gradient);
  display: flex; align-items: center; justify-content: center;
  color: white; font-weight: 700; font-size: 14px; cursor: pointer;
  transition: all .2s; margin-top: 4px; flex-shrink: 0;
  &:hover { transform: scale(1.08); box-shadow: 0 2px 12px rgba(165,145,239,.4); }
  img { width:100%; height:100%; object-fit:cover; border-radius:50%; }
`;

// ── EXTENDED PANEL (220 px) ─────────────────────
const ExtPanel = styled.div`
  width: 250px; height: 100vh; background: var(--surface);
  border-right: 1px solid var(--border);
  display: flex; flex-direction: column; overflow: hidden;
`;

const PanelHead = styled.div`
  padding: 14px 16px; border-bottom: 1px solid var(--border); flex-shrink: 0;
  display: flex; align-items: center; gap: 12px;
`;

const DevNameRow = styled.div`
  display: flex; align-items: center; gap: 8px;
  font-size: 13px; font-weight: 700; color: var(--text-primary);
  button { background:none; border:none; cursor:pointer; color:var(--text-muted);
    padding:2px; display:flex; transition:color .15s; &:hover{color:var(--primary);} }
`;

const DevSubInfo = styled.div`
  font-size: 10px; color: var(--text-muted); font-family: monospace; margin-top: 2px;
`;

const DevIgBadge = styled.div`
  display: flex; align-items: center; gap: 4px; margin-top: 4px;
  font-size: 11px; font-weight: 600; color: var(--accent);
  svg { opacity: .7; }
`;

const NavScroll = styled.div`
  flex: 1; overflow-y: auto; padding: 8px 0;
  &::-webkit-scrollbar { width: 4px; }
  &::-webkit-scrollbar-track { background: transparent; }
  &::-webkit-scrollbar-thumb { background: var(--border); border-radius: 2px; }
`;

const SectionHead = styled.button`
  display: flex; align-items: center; gap: 10px; width: 100%;
  padding: 10px 16px; background: ${p => p.$act ? 'rgba(165,145,239,.12)' : 'none'}; border: none; cursor: pointer;
  border-left: 3px solid ${p => p.$act ? 'var(--primary)' : 'transparent'};
  color: ${p => p.$act ? 'var(--primary)' : 'var(--text-secondary)'}; font-size: 14px; font-weight: 600; transition: all .15s;
  &:hover { color: var(--primary); background: var(--surface-hover); }
`;

const ChevronToggle = styled.span`
  margin-left: auto; display: flex; align-items: center; justify-content: center;
  padding: 2px 4px; border-radius: 4px; opacity: .5; cursor: pointer;
  &:hover { opacity: 1; background: var(--surface); }
`;

const NavBtn = styled.button`
  display: flex; align-items: center; gap: 10px; width: 100%;
  padding: 9px 16px; padding-left: ${p => p.$sub ? '40px' : '16px'};
  background: ${p => p.$act ? 'rgba(165,145,239,.12)' : 'transparent'};
  border: none; border-left: 3px solid ${p => p.$act ? 'var(--primary)' : 'transparent'};
  cursor: pointer; color: ${p => p.$act ? 'var(--primary)' : 'var(--text-secondary)'};
  font-size: 13.5px; font-weight: ${p => p.$act ? '600' : '500'};
  transition: all .15s; text-align: left;
  &:hover { color: var(--primary);
    background: ${p => p.$act ? 'rgba(165,145,239,.15)' : 'var(--surface-hover)'}; }
  svg { flex-shrink: 0; opacity: ${p => p.$act ? '1' : '.6'}; }
`;

const SubWrap = styled.div`
  overflow: hidden; max-height: ${p => p.$open ? '600px' : '0'}; transition: max-height .3s ease;
`;

const NavDiv = styled.div`
  height: 1px; background: var(--border); margin: 6px 16px;
`;

const UserFoot = styled.div`
  flex-shrink: 0; border-top: 1px solid var(--border);
  padding: ${p => p.$collapsed ? '8px 0' : '10px 8px'};
  display: flex; align-items: center;
  justify-content: ${p => p.$collapsed ? 'center' : 'flex-start'};
  gap: ${p => p.$collapsed ? '0' : '8px'};
  overflow: hidden;
`;

const UName = styled.div`
  font-size: 12px; font-weight: 600; color: var(--text-primary);
  overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
`;

const URole = styled.div`
  font-size: 10px; font-weight: 600; color: var(--primary);
  text-transform: uppercase; letter-spacing: .5px;
  display: flex; align-items: center; gap: 3px;
`;

const LogoutBtn = styled.button`
  background: none; border: none; cursor: pointer; color: var(--text-muted);
  padding: 4px; border-radius: 6px; display: flex; transition: all .15s;
  &:hover { color: var(--error); background: rgba(239,68,68,.1); }
`;

const GroupModeBar = styled.div`
  display: flex; gap: 2px; width: calc(100% - 16px); flex-shrink: 0; margin-bottom: 2px;
`;

const GroupModeBtn = styled.button`
  flex: 1; padding: 3px 0; border: none; border-radius: 4px; cursor: pointer;
  font-size: 9px; font-weight: 700; transition: all .15s;
  background: ${p => p.$act ? 'var(--primary)' : 'transparent'};
  color: ${p => p.$act ? '#fff' : 'var(--text-muted)'};
  &:hover { background: ${p => p.$act ? 'var(--primary)' : 'var(--surface-hover)'}; color: ${p => p.$act ? '#fff' : 'var(--primary)'}; }
`;

/* ═══════════════════════════════════════════════
   NAV ITEM DEFINITIONS
   ═══════════════════════════════════════════════ */

const INSTA_ITEMS = [
  { id: 'accounts', label: 'Comptes', desc: 'Profil IA & infos business', icon: User },
  { id: 'targets', label: 'Automatisation', desc: 'Target, Hashtag, Feed', icon: Target },
  { id: 'cibles', label: 'Analytiques & Cibles', desc: 'Stats, interactions & gestion des cibles', icon: BarChart3 },
  { id: 'search', label: 'Recherche', desc: 'Recherche de profils', icon: Search },
];

const GMAIL_ITEMS = [
  { id: 'gmail', label: 'Compte', icon: User },
  { id: 'gmail-auto', label: 'Automatisation', icon: Zap },
];

const TIKTOK_ITEMS = [
  { id: 'tiktok-account', label: 'Compte', icon: User },
  { id: 'tiktok-auto', label: 'Automatisation', icon: Zap },
  { id: 'tiktok-analytics', label: 'Analytique', icon: BarChart3 },
];

const GOOGLE_ITEMS = [
  { id: 'google-auto', label: 'Automatisation', icon: Zap },
  { id: 'google-analytics', label: 'Analytique', icon: BarChart3 },
];

const TWITTER_ITEMS = [
  { id: 'twitter-account', label: 'Compte', icon: User },
  { id: 'twitter-auto', label: 'Automatisation', icon: Zap },
  { id: 'twitter-analytics', label: 'Analytiques & Cibles', icon: BarChart3 },
];

const TINDER_ITEMS = [
  { id: 'tinder-account', label: 'Compte', icon: User },
  { id: 'tinder-auto', label: 'Automatisation', icon: Zap },
];

// Default tab for each app section (first item = Compte)
const SECTION_DEFAULT_TAB = {
  insta: INSTA_ITEMS[0]?.id,    // 'accounts'
  gmail: 'gmail',               // Compte Gmail
  tiktok: 'tiktok-account',     // Compte TikTok
  google: 'google-auto',        // Automatisation Google
  twitter: 'twitter-account',   // Compte Twitter
  tinder: 'tinder-account',     // Compte Tinder
};

const OUTILS_ITEMS = [];

const DATA_ITEMS = [
  { id: 'statistics', label: 'Analytiques', icon: BarChart3 },
  { id: 'database', label: 'Database', icon: Database },
  { id: 'actions-log', label: 'Actions Log', icon: FileText },
  { id: 'error-logs', label: 'Error Logs', icon: AlertTriangle },
];

/* ═══════════════════════════════════════════════
   SIDEBAR CONFIG — persisted in localStorage
   ═══════════════════════════════════════════════ */

const SIDEBAR_CONFIG_KEY = 'qlickr_sidebar_config';

const DEFAULT_SIDEBAR_CONFIG = {
  iconSidebar: {
    devices: true,
    crm: true,
    'impulse-bot': true,
    'accounts-list': false,
    'workflow-builder': false,
    'script-flows': false,
    gmail: false,
    tiktok: false,
    google: false,
    twitter: false,
    tinder: false,
    'fleet-leads': false,
    data: false,
    'device-info': false,
    // 'settings' is always true, not stored
  },
  textSidebar: {
    sections: {
      'device-info': true,
      insta: true,
      gmail: true,
      tiktok: true,
      google: true,
      twitter: true,
      tinder: true,
      'fleet-leads': true,
      data: true,
      devices: false,
      crm: false,
      'impulse-bot': false,
      'workflow-builder': false,
      'script-flows': false,
    },
    bottomNav: {
      'accounts-list': true,
      'impulse-bot': true,
      'workflow-builder': true,
      devices: true,
      crm: false,
      gmail: false,
      tiktok: false,
      google: false,
      twitter: false,
      tinder: false,
      'fleet-leads': false,
      data: false,
      'device-info': false,
      'script-flows': false,
      settings: false,
    },
  },
};

const getSidebarConfig = () => {
  try {
    const saved = localStorage.getItem(SIDEBAR_CONFIG_KEY);
    if (saved) {
      const parsed = JSON.parse(saved);
      // Deep merge with defaults to ensure new keys are present
      return {
        iconSidebar: { ...DEFAULT_SIDEBAR_CONFIG.iconSidebar, ...parsed.iconSidebar },
        textSidebar: {
          sections: { ...DEFAULT_SIDEBAR_CONFIG.textSidebar.sections, ...parsed.textSidebar?.sections },
          bottomNav: { ...DEFAULT_SIDEBAR_CONFIG.textSidebar.bottomNav, ...parsed.textSidebar?.bottomNav },
        },
      };
    }
  } catch (_) {}
  return { ...DEFAULT_SIDEBAR_CONFIG };
};

/* ═══════════════════════════════════════════════
   SIDEBAR COMPONENT
   ═══════════════════════════════════════════════ */

const Sidebar = ({ activeTab, setActiveTab, onDebugToggle, onGeminiToggle, selectedDevice, onSelectDevice, mirrorOpen, activeWorkstation, onWorkstationChange, modules = [], onSidebarWidthChange }) => {
  // modules=[] means no restrictions (dev/fallback); otherwise check per-module
  const hasModule = (mod) => !modules.length || modules.includes('*') || modules.includes(mod);
  const { user, logout } = useAuth();
  const { theme } = useTheme();
  const [devices, setDevices] = useState([]);
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);
  const [narrowCollapsed, setNarrowCollapsed] = useState(() => {
    try { return localStorage.getItem('qlickr_narrow_collapsed') === 'true'; } catch (_) { return false; }
  });
  const [allDeviceRows, setAllDeviceRows] = useState([]); // raw rows (multiple per device)
  const [groups, setGroups] = useState([]);
  const [sidebarConfig, setSidebarConfig] = useState(getSidebarConfig);
  const [selDev, setSelDev] = useState(selectedDevice || null);
  const [accountsOpen, setAccountsOpen] = useState(false);

  // Section open state
  const [instaOpen, setInstaOpen] = useState(false);
  const [gmailOpen, setGmailOpen] = useState(false);
  const [tiktokOpen, setTiktokOpen] = useState(false);
  const [googleOpen, setGoogleOpen] = useState(false);
  const [twitterOpen, setTwitterOpen] = useState(false);
  const [tinderOpen, setTinderOpen] = useState(false);
  const [, setOutilsOpen] = useState(false);
  const [dataOpen, setDataOpen] = useState(false);
  const [, setShowGroupInput] = useState(false);
  const [dragDeviceId, setDragDeviceId] = useState(null);
  const [dropGroupId, setDropGroupId] = useState(null);
  const [expandedGroups, setExpandedGroups] = useState({});
  const [dragInGroup, setDragInGroup] = useState(null); // { groupId, deviceId }
  const [dragOverIdx, setDragOverIdx] = useState(null); // index in group to drop before
  const [groupMode, setGroupModeState] = useState('custom'); // 'custom' | 'workflow' | 'type'
  const [ctxMenu, setCtxMenu] = useState(null); // { x, y, type, id, name }
  const [editingId, setEditingId] = useState(null); // device_id or group_id being renamed
  const [editingName, setEditingName] = useState('');
  const [connectedOnly, setConnectedOnly] = useState(false);
  const ctxRef = useRef(null);
  const [currentWsName, setCurrentWsName] = useState('Ce PC');
  const [currentWsId, setCurrentWsId] = useState(null);
  const [workstationsList, setWorkstationsList] = useState([]);
  const pendingAutoSelect = useRef(null);
  const [wsDropOpen, setWsDropOpen] = useState(false);
  const wsDropRef = useRef(null);
  const [runningDeviceIds, setRunningDeviceIds] = useState(new Set());
  const [mobileOpen, setMobileOpen] = useState(false);

  const me = {
    name: user?.displayName || user?.username || 'Admin',
    role: user?.role || 'admin',
    avatar: user?.avatar || null
  };

  // ── Collapse toggle ──
  const toggleNarrowCollapse = useCallback(() => {
    setNarrowCollapsed(prev => {
      const next = !prev;
      localStorage.setItem('qlickr_narrow_collapsed', String(next));
      const totalWidth = (next ? NARROW_COLLAPSED : NARROW_EXPANDED) + 250;
      if (onSidebarWidthChange) onSidebarWidthChange(totalWidth);
      return next;
    });
  }, [onSidebarWidthChange]);

  // Notify parent of initial width
  useEffect(() => {
    const totalWidth = (narrowCollapsed ? NARROW_COLLAPSED : NARROW_EXPANDED) + 250;
    if (onSidebarWidthChange) onSidebarWidthChange(totalWidth);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Load devices with accounts (deduplicated) ──
  const loadDevices = useCallback(async () => {
    try {
      const r = await fetch('/api/all-devices-with-accounts');
      const d = await r.json();
      const raw = Array.isArray(d) ? d.filter(dev => dev.device_id) : [];
      setAllDeviceRows(raw); // keep all rows for multi-account lookup
      // Deduplicate by device_id (API may return multiple rows per device)
      const seen = new Map();
      raw.forEach(dev => {
        if (!seen.has(dev.device_id)) seen.set(dev.device_id, dev);
      });
      const list = Array.from(seen.values());
      console.log('[Sidebar] loadDevices:', list.length, 'devices loaded, groupMode:', groupMode);
      setDevices(list);
      // Auto-select: restore last selected device from localStorage, or fallback to first
      setSelDev(prev => {
        if (!prev && list.length > 0) {
          const savedId = localStorage.getItem('selectedDeviceId');
          const restored = savedId ? list.find(d => d.device_id === savedId) : null;
          const pick = restored || list[0];
          pendingAutoSelect.current = pick;
          return pick;
        }
        return prev;
      });
    } catch (e) { console.error('[Sidebar] loadDevices error:', e); }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Load groups (+ restore expanded state from is_collapsed) ──
  const loadGroups = useCallback(async () => {
    try {
      const r = await fetch('/api/device-groups');
      const d = await r.json();
      const list = Array.isArray(d) ? d : [];
      setGroups(list);
      // Init expandedGroups — default ALL collapsed on first load
      setExpandedGroups(prev => {
        if (Object.keys(prev).length > 0) return prev;
        const map = {};
        list.forEach(g => { map[g.id] = false; });
        return map;
      });
    } catch (e) { /* silent */ }
  }, []);

  // ── Load preferences (groupMode, etc.) ──
  const loadPreferences = useCallback(async () => {
    try {
      const r = await fetch('/api/device-groups/preferences');
      const prefs = await r.json();
      if (prefs.group_mode) setGroupModeState(prefs.group_mode);
    } catch (e) { /* silent */ }
  }, []);

  // Wrapper to persist groupMode to DB
  const setGroupMode = useCallback((mode) => {
    setGroupModeState(mode);
    fetch('/api/device-groups/preferences', {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ key: 'group_mode', value: mode })
    }).catch(() => {});
  }, []);

  // Flush pending auto-select outside render
  useEffect(() => {
    if (pendingAutoSelect.current) {
      onSelectDevice?.(pendingAutoSelect.current);
      pendingAutoSelect.current = null;
    }
  });

  // Fetch workstation info FIRST, then load devices
  useEffect(() => {
    const init = async () => {
      try {
        const r = await fetch('/api/workstations');
        const data = await r.json();
        const list = Array.isArray(data) ? data : (data.workstations || []);
        if (list.length > 0) {
          setWorkstationsList(list);
          const me = list.find(w => w.is_current);
          if (me) { setCurrentWsName(me.name || 'Ce PC'); setCurrentWsId(me.id); }
        }
      } catch (_) {}
      // Now load devices (workstation info is set)
      loadDevices();
      loadGroups();
      loadPreferences();
    };
    init();
    const iv = setInterval(loadDevices, 15000);
    // Refresh workstation list every 30s for online status
    const wsIv = setInterval(async () => {
      try {
        const r = await fetch('/api/workstations');
        const data = await r.json();
        const list = Array.isArray(data) ? data : (data.workstations || []);
        if (list.length > 0) setWorkstationsList(list);
      } catch (_) {}
    }, 30000);
    // Poll running processes every 10s to show script-running indicator on device bubbles
    const loadRunning = async () => {
      try {
        const r = await fetch('/api/processes/all');
        const data = await r.json();
        const procs = data.processes || [];
        const ids = new Set(procs.filter(p => p.status === 'running' && p.device_id).map(p => p.device_id));
        setRunningDeviceIds(ids);
      } catch (_) {}
    };
    loadRunning();
    const procIv = setInterval(loadRunning, 10000);
    // Listen for sidebar config changes from Settings page
    const handleSidebarConfigChange = () => setSidebarConfig(getSidebarConfig());
    window.addEventListener('sidebarConfigChanged', handleSidebarConfigChange);
    return () => { clearInterval(iv); clearInterval(wsIv); clearInterval(procIv); window.removeEventListener('sidebarConfigChanged', handleSidebarConfigChange); };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Compute badge label based on active workstation filter from Devices page
  const badgeLabel = (() => {
    if (!activeWorkstation || activeWorkstation === 'local') return currentWsName;
    if (activeWorkstation === 'all') return 'Tous';
    const ws = workstationsList.find(w => w.id?.toString() === activeWorkstation?.toString());
    return ws?.name || currentWsName;
  })();

  // Close dropdown on outside click
  useEffect(() => {
    const closeWsDrop = (e) => {
      if (wsDropRef.current && !wsDropRef.current.contains(e.target)) setWsDropOpen(false);
    };
    if (wsDropOpen) document.addEventListener('mousedown', closeWsDrop);
    return () => document.removeEventListener('mousedown', closeWsDrop);
  }, [wsDropOpen]);

  // Switch workstation handler
  const switchWorkstation = (wsValue) => {
    onWorkstationChange?.(wsValue);
    setWsDropOpen(false);
    // If selecting a remote, trigger device check
    if (wsValue !== 'all' && wsValue !== 'local') {
      fetch(`/api/workstations/${wsValue}/check-devices`, { method: 'POST' }).catch(() => {});
    }
  };

  // Filter devices based on selected workstation
  const filteredDevices = React.useMemo(() => {
    if (!activeWorkstation || activeWorkstation === 'local') {
      // In SQLite/standalone mode, currentWsId is 'local' (string) — show all devices
      if (!currentWsId || currentWsId === 'local') return devices;
      return devices.filter(d => Number(d.workstation_id) === Number(currentWsId));
    }
    if (activeWorkstation === 'all') return devices;
    const wsId = parseInt(activeWorkstation, 10);
    const ws = workstationsList.find(w => w.id === wsId);
    const remoteRealId = ws?.remote_ws_id;
    return devices.filter(d => {
      const matchLocal = Number(d.workstation_id) === wsId;
      const matchRemote = remoteRealId != null && Number(d.workstation_id) === Number(remoteRealId);
      return matchLocal || matchRemote;
    });
  }, [devices, activeWorkstation, currentWsId, workstationsList]);

  // Sync from parent
  useEffect(() => {
    if (selectedDevice) setSelDev(selectedDevice);
  }, [selectedDevice]);

  const selectDevice = (d) => {
    setSelDev(d);
    onSelectDevice?.(d);
    if (d?.device_id) localStorage.setItem('selectedDeviceId', d.device_id);
    // Close the mobile sidebar overlay after selecting a device
    if (typeof window !== 'undefined' && window.innerWidth <= 768) {
      setMobileOpen(false);
    }
    // Stay on current page — just update the selected device context
  };

  // Get all accounts for the currently selected device
  const currentDeviceAccounts = React.useMemo(() => {
    if (!selDev?.device_id) return [];
    return allDeviceRows.filter(r => r.device_id === selDev.device_id && r.account_id);
  }, [allDeviceRows, selDev?.device_id]);

  // Switch to a different account on the same device
  const switchAccount = (accountRow) => {
    const updated = { ...selDev, account_id: accountRow.account_id, instagram_username: accountRow.instagram_username, app_id: accountRow.app_id, account_type: accountRow.account_type };
    setSelDev(updated);
    onSelectDevice?.(updated);
    nav('accounts');
  };

  const nav = (id) => { setActiveTab(id); setMobileOpen(false); };
  const is = (id) => activeTab === id;

  // Auto-expand section when its child is active
  useEffect(() => {
    if (INSTA_ITEMS.some(i => i.id === activeTab)) setInstaOpen(true);
    if (GMAIL_ITEMS.some(i => i.id === activeTab)) setGmailOpen(true);
    if (TIKTOK_ITEMS.some(i => i.id === activeTab)) setTiktokOpen(true);
    if (GOOGLE_ITEMS.some(i => i.id === activeTab)) setGoogleOpen(true);
    if (TWITTER_ITEMS.some(i => i.id === activeTab)) setTwitterOpen(true);
    if (TINDER_ITEMS.some(i => i.id === activeTab)) setTinderOpen(true);
    if (OUTILS_ITEMS.some(i => i.id === activeTab)) setOutilsOpen(true);
    if (DATA_ITEMS.some(i => i.id === activeTab)) setDataOpen(true);
  }, [activeTab]);

  // ── Drag & Drop ───────────────────────────────
  const handleDragStart = (e, deviceId) => {
    setDragDeviceId(deviceId);
    e.dataTransfer.setData('text/plain', deviceId);
    e.dataTransfer.effectAllowed = 'move';
    // Create clean drag ghost to avoid sibling images bleeding through
    const ghost = e.currentTarget.cloneNode(true);
    ghost.style.position = 'absolute';
    ghost.style.top = '-9999px';
    ghost.style.left = '-9999px';
    ghost.style.width = '52px';
    ghost.style.height = '52px';
    ghost.style.borderRadius = '50%';
    ghost.style.overflow = 'hidden';
    document.body.appendChild(ghost);
    e.dataTransfer.setDragImage(ghost, 26, 26);
    requestAnimationFrame(() => document.body.removeChild(ghost));
  };
  const handleDragOver = (e, groupId) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDropGroupId(groupId);
  };
  const handleDragLeave = () => setDropGroupId(null);
  const handleDrop = async (e, toGroupId) => {
    e.preventDefault();
    e.stopPropagation();
    setDropGroupId(null);
    // Read from dataTransfer (reliable) — state may be cleared by dragEnd race
    const devId = e.dataTransfer.getData('text/plain') || dragDeviceId;
    if (!devId) return;
    try {
      const r = await fetch('/api/device-groups/move-device', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ device_id: devId, to_group_id: toGroupId || null })
      });
      if (r.ok) {
        loadGroups();
        loadDevices();
      }
    } catch (e) { /* silent */ }
    setDragDeviceId(null);
  };
  const handleDragEnd = () => { setDragDeviceId(null); setDropGroupId(null); setDragInGroup(null); setDragOverIdx(null); };

  // ── Intra-group reorder via drag ───────────────
  const reorderInGroup = async (groupId, deviceIds) => {
    try {
      await fetch('/api/device-groups/reorder-members', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ group_id: groupId, device_ids: deviceIds })
      });
      loadGroups();
    } catch (e) { /* silent */ }
  };

  const handleIntraGroupDragStart = (e, groupId, deviceId) => {
    setDragInGroup({ groupId, deviceId });
    setDragDeviceId(deviceId);
    e.dataTransfer.setData('text/plain', deviceId);
    e.dataTransfer.effectAllowed = 'move';
    const ghost = e.currentTarget.cloneNode(true);
    ghost.style.position = 'absolute'; ghost.style.top = '-9999px'; ghost.style.left = '-9999px';
    ghost.style.width = '52px'; ghost.style.height = '52px'; ghost.style.borderRadius = '50%'; ghost.style.overflow = 'hidden';
    document.body.appendChild(ghost);
    e.dataTransfer.setDragImage(ghost, 26, 26);
    requestAnimationFrame(() => document.body.removeChild(ghost));
  };

  const handleIntraGroupDragOver = (e, idx) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverIdx(idx);
  };

  const handleIntraGroupDrop = (e, groupId, orderedDeviceIds) => {
    e.preventDefault();
    e.stopPropagation();
    const srcId = e.dataTransfer.getData('text/plain') || dragInGroup?.deviceId;
    if (!srcId || !groupId) return;
    const filtered = orderedDeviceIds.filter(id => id !== srcId);
    const insertIdx = dragOverIdx != null ? Math.min(dragOverIdx, filtered.length) : filtered.length;
    filtered.splice(insertIdx, 0, srcId);
    reorderInGroup(groupId, filtered);
    setDragInGroup(null); setDragOverIdx(null); setDragDeviceId(null);
  };

  // ── Context menu ──────────────────────────────
  const openCtx = (e, type, id, name) => {
    e.preventDefault();
    setCtxMenu({ x: e.clientX, y: e.clientY, type, id, name });
  };
  useEffect(() => {
    const close = (e) => {
      if (ctxRef.current && !ctxRef.current.contains(e.target)) setCtxMenu(null);
    };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, []);

  const startRename = () => {
    if (!ctxMenu) return;
    setEditingId(ctxMenu.id);
    setEditingName(ctxMenu.name || '');
    setCtxMenu(null);
  };
  const confirmRename = async () => {
    if (!editingName.trim() || !editingId) { setEditingId(null); return; }
    // Detect if it's a group id (number) or device id (string)
    if (typeof editingId === 'number') {
      await fetch(`/api/device-groups/${editingId}`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: editingName.trim() })
      });
      loadGroups();
    } else {
      await fetch('/api/device-groups/rename-device', {
        method: 'PUT', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ device_id: editingId, name: editingName.trim() })
      });
      loadDevices();
    }
    setEditingId(null);
  };
  const deleteGroup = async () => {
    if (!ctxMenu) return;
    await fetch(`/api/device-groups/${ctxMenu.id}`, { method: 'DELETE' });
    loadGroups();
    setCtxMenu(null);
  };
  const changeGroupColor = async (groupId, color) => {
    try {
      await fetch(`/api/device-groups/${groupId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ color })
      });
      loadGroups();
    } catch (e) { /* silent */ }
    setCtxMenu(null);
  };

  // ── Build grouped device list for narrow bar ──
  const groupedDeviceIds = new Set(groups.flatMap(g => (g.members || []).map(m => m.device_id)));
  const ungrouped = filteredDevices.filter(d => !groupedDeviceIds.has(d.device_id));

  // ── Auto-group by workflow or account_type ──
  const WORKFLOW_COLORS = { A: '#a591ef', B: '#3b82f6', C: '#22c55e', D: '#f59e0b', E: '#ef4444', F: '#ec4899', G: '#14b8a6', H: '#f97316' };
  const TYPE_COLORS = {
    client: '#3b82f6', CLIENT: '#3b82f6',
    our: '#22c55e', OUR: '#22c55e',
    standard: '#6b7280', STANDARD: '#6b7280',
    premium: '#f59e0b', PREMIUM: '#f59e0b',
    bot: '#ef4444', BOT: '#ef4444',
    test: '#8b5cf6', TEST: '#8b5cf6',
    burner: '#f97316', BURNER: '#f97316',
    personal: '#ec4899', PERSONAL: '#ec4899',
    business: '#06b6d4', BUSINESS: '#06b6d4',
    creator: '#14b8a6', CREATOR: '#14b8a6',
    'N/A': '#6366f1',
  };

  const autoGroups = React.useMemo(() => {
    if (groupMode === 'custom') return null;
    const key = groupMode === 'workflow' ? 'workflow' : 'account_type';
    const colorMap = groupMode === 'workflow' ? WORKFLOW_COLORS : TYPE_COLORS;
    const map = {};
    filteredDevices.forEach(d => {
      const val = d[key] || 'N/A';
      if (!map[val]) map[val] = { name: val, key: val, isWorkflow: groupMode === 'workflow', color: colorMap[val] || colorMap[val.toLowerCase()] || '#6366f1', devices: [] };
      map[val].devices.push(d);
    });
    return Object.entries(map).sort((a, b) => a[0].localeCompare(b[0])).map(([k, v]) => ({ id: `auto-${k}`, ...v }));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groupMode, filteredDevices]);

  // Theme-adaptive logo: dark themes → white logo, light themes → black logo
  const logoSrc = theme?.isDark ? '/Qlickr-Blanc.png' : '/Qlickr-Noir.png';

  // Device is online only if connected AND tagged with matching workstation
  const isDeviceOnline = (d) => {
    const raw = d.isConnected || d.is_online;
    if (!raw) return false;
    // "Tous": show real connection status for ALL workstations
    if (activeWorkstation === 'all') return raw;
    // When viewing a remote workstation, green = belongs to that remote
    if (activeWorkstation && activeWorkstation !== 'local') {
      const wsId = parseInt(activeWorkstation, 10);
      const ws = workstationsList.find(w => w.id === wsId);
      const remoteRealId = ws?.remote_ws_id;
      return Number(d.workstation_id) === wsId || (remoteRealId != null && Number(d.workstation_id) === Number(remoteRealId));
    }
    // Local: only show green for devices tagged with OUR ws
    if (currentWsId != null) {
      // In SQLite/standalone mode, currentWsId is 'local' — all devices are local
      if (currentWsId === 'local') return raw;
      return Number(d.workstation_id) === Number(currentWsId);
    }
    return raw;
  };

  const renderDeviceThumb = (d, idx, { inGroup = false, groupId = null } = {}) => {
    const id = d.device_id;
    const online = isDeviceOnline(d);
    const isSel = selDev?.device_id === id;
    const initials = (d.device_name || id || '??').substring(0, 2).toUpperCase();
    const isEditing = editingId === id;
    const hasRunningScript = runningDeviceIds.has(id);
    const localPic = d.instagram_username ? getLocalProfilePicUrl(d.instagram_username) : null;
    const serverPic = d.instagram_username ? getProfilePicUrl(d.instagram_username) : null;
    const picUrl = localPic || d.profile_pic_url || null;
    const devName = d.device_name || id || '??';
    const isMini = narrowCollapsed || !inGroup; // mini in collapsed & in flat list; full in group expanded

    const thumb = (
      <DeviceThumb
        $sel={isSel}
        $online={online}
        $dragging={dragDeviceId === id}
        $mini={isMini}
        draggable
        onDragStart={e => inGroup && groupId ? handleIntraGroupDragStart(e, groupId, id) : handleDragStart(e, id)}
        onDragEnd={handleDragEnd}
        onClick={() => selectDevice(d)}
        onContextMenu={e => openCtx(e, 'device', id, devName)}
        title={`${devName}\n${d.instagram_username ? '@' + d.instagram_username : ''}${hasRunningScript ? '\n⚡ Script en cours' : ''}`}
      >
        {hasRunningScript && <ScriptRunningRing />}
        {isEditing ? (
          <InlineInput
            autoFocus
            value={editingName}
            onChange={e => setEditingName(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') confirmRename(); if (e.key === 'Escape') setEditingId(null); }}
            onBlur={confirmRename}
            onClick={e => e.stopPropagation()}
            style={{ width: isMini ? 30 : 42, fontSize: 9, padding: 2, textAlign: 'center' }}
          />
        ) : picUrl ? (
          <ThumbImg src={picUrl} alt="" draggable="false" onError={e => {
            const step = parseInt(e.target.dataset.fallback || '0');
            if (step === 0 && serverPic) { e.target.dataset.fallback = '1'; e.target.src = serverPic; }
            else if (step <= 1 && d.instagram_username) { e.target.dataset.fallback = '2'; e.target.src = `https://unavatar.io/instagram/${d.instagram_username}`; }
            else { e.target.style.display = 'none'; e.target.parentElement.innerText = initials; }
          }} />
        ) : initials}
        <OnlineDot $on={online} />
      </DeviceThumb>
    );

    // EXPANDED: inline row (thumbnail + name + IG handle)
    if (!narrowCollapsed) {
      return (
        <DeviceSlot key={`dev-${id}-${idx}`} $inline $sel={isSel} onClick={() => selectDevice(d)}>
          {thumb}
          <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
            <DeviceSlotName $sel={isSel}>{devName}</DeviceSlotName>
            {d.instagram_username && <DeviceSlotIg>@{d.instagram_username}</DeviceSlotIg>}
          </div>
          {hasRunningScript && <Zap size={11} style={{ color: 'var(--primary)', flexShrink: 0 }} />}
        </DeviceSlot>
      );
    }

    // COLLAPSED: just tiny circle
    return (
      <DeviceSlot key={`dev-${id}-${idx}`}>
        {thumb}
      </DeviceSlot>
    );
  };

  return (
    <>
      {/* Mobile hamburger button */}
      <MobileHamburger onClick={() => setMobileOpen(prev => !prev)}>
        <Layers size={20} />
      </MobileHamburger>

      {/* Mobile overlay backdrop */}
      <MobileOverlay $open={mobileOpen} onClick={() => setMobileOpen(false)} />

      {/* Mobile bottom navigation */}
      <MobileBottomNav>
        <MobileNavItem $active={is('impulse-bot')} onClick={() => nav('impulse-bot')}>
          <Zap size={20} /> Bot
        </MobileNavItem>
        <MobileNavItem $active={is('devices')} onClick={() => nav('devices')}>
          <Smartphone size={20} /> Devices
        </MobileNavItem>
        <MobileNavItem $active={is('statistics')} onClick={() => nav('statistics')}>
          <TrendingUp size={20} /> Stats
        </MobileNavItem>
        <MobileNavItem $active={is('workflow-builder')} onClick={() => nav('workflow-builder')}>
          <GitBranch size={20} /> Flows
        </MobileNavItem>
        <MobileNavItem $active={is('settings')} onClick={() => nav('settings')}>
          <Settings size={20} /> Config
        </MobileNavItem>
      </MobileBottomNav>

    <SidebarWrapper $mobileOpen={mobileOpen}>
      {/* ===== NARROW BAR ===== */}
      <NarrowBar $collapsed={narrowCollapsed}>
        <BrandLogo $collapsed={narrowCollapsed} onClick={() => window.location.reload()}>
          <img src={logoSrc} alt="QLICKR" />
        </BrandLogo>
        <NDivider />

        <DeviceListWrap $collapsed={narrowCollapsed}>
          {/* Sticky filter bar - stays fixed at top when scrolling */}
          <StickyFilterBar $collapsed={narrowCollapsed}>
            {/* Workstation badge */}
            <div ref={wsDropRef} style={{ position: 'relative', flexShrink: 0 }}>
              <WorkstationBadge
                title={`Workstation: ${badgeLabel} — Cliquer pour changer`}
                onClick={() => setWsDropOpen(v => !v)}
              >
                {badgeLabel} <ChevronDown size={8} style={{ marginLeft: 2, opacity: .6 }} />
              </WorkstationBadge>
              {wsDropOpen && (
                <WsDropdown>
                  <WsDropItem $active={!activeWorkstation || activeWorkstation === 'local'} onClick={() => switchWorkstation('local')}>
                    <WsDropDot $online={true} />
                    {currentWsName || 'Ce PC'}
                    <WsDropCount>{currentWsId === 'local' ? devices.length : devices.filter(d => currentWsId && Number(d.workstation_id) === Number(currentWsId)).length} dev</WsDropCount>
                  </WsDropItem>
                  <WsDropItem $active={activeWorkstation === 'all'} onClick={() => switchWorkstation('all')}>
                    <Server size={10} style={{ opacity: .6 }} />
                    Tous
                    <WsDropCount>{devices.length} dev</WsDropCount>
                  </WsDropItem>
                  {workstationsList.filter(w => !w.is_current).map(w => (
                    <WsDropItem key={w.id} $active={activeWorkstation === w.id.toString()} onClick={() => switchWorkstation(w.id.toString())}>
                      <WsDropDot $online={w.live_connected || w.is_online} />
                      {w.name}
                      <WsDropCount>{w.device_count || 0} dev</WsDropCount>
                    </WsDropItem>
                  ))}
                </WsDropdown>
              )}
            </div>
            {/* Group mode toggle */}
            <GroupModeBar>
              <GroupModeBtn $act={groupMode === 'custom'} onClick={() => setGroupMode('custom')} title="Groupes personnalisés">G</GroupModeBtn>
              <GroupModeBtn $act={groupMode === 'workflow'} onClick={() => setGroupMode('workflow')} title="Par workflow">W</GroupModeBtn>
              <GroupModeBtn $act={groupMode === 'type'} onClick={() => setGroupMode('type')} title="Par type de compte">T</GroupModeBtn>
              <GroupModeBtn $act={connectedOnly} onClick={() => setConnectedOnly(v => !v)} title="Connectés uniquement" style={connectedOnly ? { background: 'rgba(34,197,94,.25)', color: '#22c55e', borderColor: '#22c55e' } : {}}>●</GroupModeBtn>
            </GroupModeBar>
          </StickyFilterBar>

          {groupMode === 'custom' && !narrowCollapsed ? (<>
            {/* Custom groups — Discord-style colored bubbles */}
            {groups.map(g => {
              const members = g.members || [];
              const orderMap = {};
              members.forEach((m, i) => { orderMap[m.device_id] = m.sort_order != null ? m.sort_order : i; });
              const memberDevices = filteredDevices.filter(d =>
                members.some(m => m.device_id === d.device_id)
              ).filter(d => !connectedOnly || isDeviceOnline(d))
               .sort((a, b) => (orderMap[a.device_id] ?? 999) - (orderMap[b.device_id] ?? 999));
              const isEditingGroup = editingId === g.id;
              const isExpanded = expandedGroups[g.id] === true;
              const toggleGroup = (e) => {
                if (isEditingGroup) return;
                e.stopPropagation();
                const newExpanded = !isExpanded;
                setExpandedGroups(prev => ({ ...prev, [g.id]: newExpanded }));
                fetch(`/api/device-groups/${g.id}`, {
                  method: 'PUT',
                  headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify({ is_collapsed: !newExpanded })
                }).catch(() => {});
              };
              return (
                <React.Fragment key={`grp-${g.id}`}>
                  <GroupBubble
                    $color={g.color || '#a591ef'}
                    $expanded={isExpanded}
                    $dropTarget={dropGroupId === g.id}
                    onClick={toggleGroup}
                    onDragOver={e => handleDragOver(e, g.id)}
                    onDragLeave={handleDragLeave}
                    onDrop={e => {
                      handleDrop(e, g.id);
                      setExpandedGroups(prev => ({ ...prev, [g.id]: true }));
                      fetch(`/api/device-groups/${g.id}`, {
                        method: 'PUT',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({ is_collapsed: false })
                      }).catch(() => {});
                    }}
                    onContextMenu={e => openCtx(e, 'group', g.id, g.name)}
                    title={g.name + (memberDevices.length ? ` (${memberDevices.length})` : '')}
                  >
                    {isEditingGroup ? (
                      <InlineInput
                        autoFocus
                        value={editingName}
                        onChange={e => setEditingName(e.target.value)}
                        onKeyDown={e => { if (e.key === 'Enter') confirmRename(); if (e.key === 'Escape') setEditingId(null); }}
                        onBlur={confirmRename}
                        onClick={e => e.stopPropagation()}
                        style={{ width: 60, fontSize: 9, padding: 2, textAlign: 'center' }}
                      />
                    ) : (<>
                      <GroupName>{g.name || '??'}</GroupName>
                      <GroupCounts>
                        <CountBadge $green>{memberDevices.filter(d => isDeviceOnline(d)).length}</CountBadge>
                        <CountBadge>{memberDevices.filter(d => !isDeviceOnline(d)).length}</CountBadge>
                      </GroupCounts>
                      {!isExpanded && memberDevices.length > 0 && (
                        <CollapsedPics>
                          {memberDevices.slice(0, 6).map(d => {
                            const onl = isDeviceOnline(d);
                            const hasRun = runningDeviceIds.has(d.device_id);
                            const localPic = d.instagram_username ? getLocalProfilePicUrl(d.instagram_username) : null;
                            const srvPic = d.instagram_username ? getProfilePicUrl(d.instagram_username) : null;
                            const picUrl = localPic || d.profile_pic_url || null;
                            const initials = (d.device_name || d.device_id || '??').substring(0, 2).toUpperCase();
                            const pic = picUrl ? (
                              <MiniPic src={picUrl} $online={onl} alt=""
                                onError={e => {
                                  const step = parseInt(e.target.dataset.fallback || '0');
                                  if (step === 0 && srvPic) { e.target.dataset.fallback = '1'; e.target.src = srvPic; }
                                  else { e.target.style.display = 'none'; }
                                }} />
                            ) : (
                              <MiniPicFallback $online={onl}>{initials}</MiniPicFallback>
                            );
                            return hasRun ? (
                              <MiniPicWrap key={d.device_id}>{pic}<MiniRunningRing /></MiniPicWrap>
                            ) : <React.Fragment key={d.device_id}>{pic}</React.Fragment>;
                          })}
                        </CollapsedPics>
                      )}
                    </>)}
                  </GroupBubble>
                  {isExpanded && memberDevices.length > 0 && (
                    <GroupDevicesWrap $color={g.color || '#a591ef'}
                      onDragOver={e => { e.preventDefault(); e.dataTransfer.dropEffect = 'move'; }}
                      onDrop={e => handleIntraGroupDrop(e, g.id, memberDevices.map(d => d.device_id))}
                    >
                      {memberDevices.map((d, idx) => (
                        <React.Fragment key={`slot-${d.device_id}`}>
                          <DropLine
                            $active={dragInGroup?.groupId === g.id && dragOverIdx === idx}
                            onDragOver={e => handleIntraGroupDragOver(e, idx)}
                          />
                          {renderDeviceThumb(d, idx, { inGroup: true, groupId: g.id })}
                        </React.Fragment>
                      ))}
                      <DropLine
                        $active={dragInGroup?.groupId === g.id && dragOverIdx === memberDevices.length}
                        onDragOver={e => handleIntraGroupDragOver(e, memberDevices.length)}
                      />
                    </GroupDevicesWrap>
                  )}
                </React.Fragment>
              );
            })}

            {/* Ungrouped — drop zone separator */}
            {groups.length > 0 && (
              <GroupSep
                $dropTarget={dropGroupId === 'ungrouped'}
                onDragOver={e => handleDragOver(e, 'ungrouped')}
                onDragLeave={handleDragLeave}
                onDrop={e => handleDrop(e, null)}
                style={{ opacity: ungrouped.length > 0 || dragDeviceId ? 1 : 0.3 }}
              />
            )}
            {(connectedOnly ? ungrouped.filter(d => isDeviceOnline(d)) : ungrouped).map((d, idx) => renderDeviceThumb(d, idx))}

            {/* New group button */}
            <NDivider />
            <NIconBtn onClick={() => setShowGroupInput(v => !v)} title="Nouveau groupe">
              <FolderPlus size={15} />
            </NIconBtn>
          </>) : !narrowCollapsed ? (
            /* Auto-grouped by workflow or account_type */
            (autoGroups || []).map(ag => {
              const isExpanded = expandedGroups[ag.id] === true;
              const filteredAgDevices = connectedOnly ? ag.devices.filter(d => isDeviceOnline(d)) : ag.devices;
              if (connectedOnly && filteredAgDevices.length === 0) return null;
              return (
                <React.Fragment key={ag.id}>
                  <GroupBubble
                    $color={ag.color}
                    $expanded={isExpanded}
                    onClick={() => setExpandedGroups(prev => ({ ...prev, [ag.id]: !isExpanded }))}
                    title={`${ag.name} (${filteredAgDevices.length})`}
                  >
                    <GroupName $big={ag.isWorkflow} $color={ag.color}>{ag.name}</GroupName>
                    <GroupCounts>
                      <CountBadge $green>{filteredAgDevices.filter(d => isDeviceOnline(d)).length}</CountBadge>
                      <CountBadge>{filteredAgDevices.filter(d => !isDeviceOnline(d)).length}</CountBadge>
                    </GroupCounts>
                    {!isExpanded && filteredAgDevices.length > 0 && (
                      <CollapsedPics>
                        {filteredAgDevices.slice(0, 6).map(d => {
                          const onl = isDeviceOnline(d);
                          const hasRun = runningDeviceIds.has(d.device_id);
                          const localPic = d.instagram_username ? getLocalProfilePicUrl(d.instagram_username) : null;
                          const srvPic = d.instagram_username ? getProfilePicUrl(d.instagram_username) : null;
                          const picUrl = localPic || d.profile_pic_url || null;
                          const initials = (d.device_name || d.device_id || '??').substring(0, 2).toUpperCase();
                          const pic = picUrl ? (
                            <MiniPic src={picUrl} $online={onl} alt=""
                              onError={e => {
                                const step = parseInt(e.target.dataset.fallback || '0');
                                if (step === 0 && srvPic) { e.target.dataset.fallback = '1'; e.target.src = srvPic; }
                                else { e.target.style.display = 'none'; }
                              }} />
                          ) : (
                            <MiniPicFallback $online={onl}>{initials}</MiniPicFallback>
                          );
                          return hasRun ? (
                            <MiniPicWrap key={d.device_id}>{pic}<MiniRunningRing /></MiniPicWrap>
                          ) : <React.Fragment key={d.device_id}>{pic}</React.Fragment>;
                        })}
                      </CollapsedPics>
                    )}
                  </GroupBubble>
                  {isExpanded && filteredAgDevices.length > 0 && (
                    <GroupDevicesWrap $color={ag.color}>
                      {filteredAgDevices.map((d, idx) => renderDeviceThumb(d, idx))}
                    </GroupDevicesWrap>
                  )}
                </React.Fragment>
              );
            })
          ) : (
            /* Collapsed mode: flat device list as mini thumbs */
            (connectedOnly ? filteredDevices.filter(d => isDeviceOnline(d)) : filteredDevices).map((d, idx) => renderDeviceThumb(d, idx))
          )}
        </DeviceListWrap>

        <NDivider />

        <NBottom $collapsed={narrowCollapsed}>
          {sidebarConfig.iconSidebar.devices !== false && (
            <NIconBtn $act={is('devices')} $collapsed={narrowCollapsed} onClick={() => nav('devices')} title="Devices">
              <LayoutGrid size={15} />{!narrowCollapsed && 'Devices'}
            </NIconBtn>
          )}
          {sidebarConfig.iconSidebar.crm !== false && (
            <NIconBtn $act={is('crm')} $collapsed={narrowCollapsed} onClick={() => nav('crm')} title="CRM">
              <Contact size={15} />{!narrowCollapsed && 'CRM'}
            </NIconBtn>
          )}
          {sidebarConfig.iconSidebar['impulse-bot'] !== false && (
            <NIconBtn $act={is('impulse-bot')} $collapsed={narrowCollapsed} onClick={() => nav('impulse-bot')} title="Centre de Contrôle">
              <LayoutDashboard size={15} />{!narrowCollapsed && 'Contrôle'}
            </NIconBtn>
          )}
          {sidebarConfig.iconSidebar['accounts-list'] && (
            <NIconBtn $act={is('accounts-list')} $collapsed={narrowCollapsed} onClick={() => nav('accounts-list')} title="Instagram">
              <Instagram size={15} />{!narrowCollapsed && 'Instagram'}
            </NIconBtn>
          )}
          {sidebarConfig.iconSidebar['workflow-builder'] && (
            <NIconBtn $act={is('workflow-builder')} $collapsed={narrowCollapsed} onClick={() => nav('workflow-builder')} title="Workflows">
              <Zap size={15} />{!narrowCollapsed && 'Workflows'}
            </NIconBtn>
          )}
          {sidebarConfig.iconSidebar['script-flows'] && (
            <NIconBtn $act={is('script-flows')} $collapsed={narrowCollapsed} onClick={() => nav('script-flows')} title="Script Flows">
              <Layers size={15} />{!narrowCollapsed && 'Flows'}
            </NIconBtn>
          )}
          {sidebarConfig.iconSidebar.gmail && hasModule('gmail') && (
            <NIconBtn $act={is('gmail-auto')} $collapsed={narrowCollapsed} onClick={() => nav('gmail-auto')} title="Gmail">
              <Mail size={15} />{!narrowCollapsed && 'Gmail'}
            </NIconBtn>
          )}
          {sidebarConfig.iconSidebar.tiktok && hasModule('tiktok') && (
            <NIconBtn $act={is('tiktok-auto')} $collapsed={narrowCollapsed} onClick={() => nav('tiktok-auto')} title="TikTok">
              <Video size={15} />{!narrowCollapsed && 'TikTok'}
            </NIconBtn>
          )}
          {sidebarConfig.iconSidebar.google && hasModule('google') && (
            <NIconBtn $act={is('google-auto')} $collapsed={narrowCollapsed} onClick={() => nav('google-auto')} title="Google">
              <Globe size={15} />{!narrowCollapsed && 'Google'}
            </NIconBtn>
          )}
          {sidebarConfig.iconSidebar.twitter && hasModule('twitter') && (
            <NIconBtn $act={is('twitter-auto')} $collapsed={narrowCollapsed} onClick={() => nav('twitter-auto')} title="Twitter/X">
              <MessageCircle size={15} />{!narrowCollapsed && 'Twitter'}
            </NIconBtn>
          )}
          {sidebarConfig.iconSidebar.tinder && hasModule('tinder') && (
            <NIconBtn $act={is('tinder-auto') || is('tinder-account')} $collapsed={narrowCollapsed} onClick={() => nav('tinder-auto')} title="Tinder">
              <Heart size={15} />{!narrowCollapsed && 'Tinder'}
            </NIconBtn>
          )}
          {sidebarConfig.iconSidebar['fleet-leads'] && (
            <NIconBtn $act={is('fleet-leads')} $collapsed={narrowCollapsed} onClick={() => nav('fleet-leads')} title="Lead Scraper Fleet">
              <TrendingUp size={15} />{!narrowCollapsed && 'Fleet Leads'}
            </NIconBtn>
          )}
          {sidebarConfig.iconSidebar.data && (
            <NIconBtn $act={is('database')} $collapsed={narrowCollapsed} onClick={() => nav('database')} title="Données">
              <Database size={15} />{!narrowCollapsed && 'Données'}
            </NIconBtn>
          )}
          {sidebarConfig.iconSidebar['device-info'] && (
            <NIconBtn $act={is('device-info')} $collapsed={narrowCollapsed} onClick={() => nav('device-info')} title="Informations">
              <Info size={15} />{!narrowCollapsed && 'Infos'}
            </NIconBtn>
          )}
          <NIconBtn $act={is('settings')} $collapsed={narrowCollapsed} onClick={() => nav('settings')} title="Paramètres">
            <Settings size={15} />{!narrowCollapsed && 'Paramètres'}
          </NIconBtn>
        </NBottom>
        <UserFoot $collapsed={narrowCollapsed}>
          <AvatarDot style={{ width: narrowCollapsed ? 28 : 30, height: narrowCollapsed ? 28 : 30, fontSize: 11 }}>
            {me.avatar ? <img src={me.avatar} alt="" /> : me.name?.charAt(0)?.toUpperCase() || 'U'}
          </AvatarDot>
          {!narrowCollapsed && (
            <div style={{ flex: 1, minWidth: 0 }}>
              <UName>{me.name}</UName>
              <URole>{me.role === 'admin' && <Crown size={9} />}{me.role}</URole>
            </div>
          )}
          {!narrowCollapsed && <LogoutBtn onClick={() => setShowLogoutConfirm(true)} title="Déconnexion"><LogOut size={14} /></LogoutBtn>}
        </UserFoot>
        <CollapseToggle onClick={toggleNarrowCollapse} title={narrowCollapsed ? 'Étendre' : 'Replier'}>
          {narrowCollapsed ? <PanelLeftOpen size={14} /> : <><PanelLeftClose size={14} /> Replier</>}
        </CollapseToggle>
      </NarrowBar>

      {/* ===== EXTENDED PANEL ===== */}
      <ExtPanel>
        <PanelHead>
          {selDev ? (
            <>
              {selDev.instagram_username ? (
                <img
                  src={getLocalProfilePicUrl(selDev.instagram_username)}
                  alt=""
                  style={{ width: 38, height: 38, borderRadius: '50%', border: '2px solid var(--primary)', objectFit: 'cover', flexShrink: 0 }}
                  onError={e => {
                    const step = parseInt(e.target.dataset.fallback || '0');
                    if (step === 0) { e.target.dataset.fallback = '1'; e.target.src = getProfilePicUrl(selDev.instagram_username); }
                    else if (step === 1) { e.target.dataset.fallback = '2'; e.target.src = `https://unavatar.io/instagram/${selDev.instagram_username}`; }
                    else { e.target.style.display = 'none'; }
                  }}
                />
              ) : (
                <div style={{ width: 38, height: 38, borderRadius: '50%', background: 'var(--surface)', border: '2px solid var(--border)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <Smartphone size={16} style={{ color: 'var(--text-muted)' }} />
                </div>
              )}
              <div style={{ minWidth: 0, flex: 1 }}>
                <DevNameRow>
                  {selDev.device_name || 'Appareil'}
                  <button onClick={() => {
                    if (selDev.device_id) navigator.clipboard.writeText(selDev.device_id);
                  }} title="Copier l'ID"><Copy size={12} /></button>
                  <button onClick={() => nav('phone-cast')} title="Screen Mirror" style={{ width: 28, height: 28, borderRadius: 6, border: '1px solid var(--border)', background: mirrorOpen ? 'var(--primary)' : 'var(--surface)', color: mirrorOpen ? '#fff' : 'var(--text-muted)', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', flexShrink: 0, marginLeft: 'auto', transition: 'all .15s' }}><Monitor size={13} /></button>
                </DevNameRow>
                <DevSubInfo>{selDev.device_id}</DevSubInfo>
                {selDev.instagram_username && (
                  <DevIgBadge>
                    <Instagram size={12} /> @{selDev.instagram_username}
                  </DevIgBadge>
                )}
              </div>
            </>
          ) : (
            <DevNameRow><Smartphone size={14} /> Aucun appareil</DevNameRow>
          )}
        </PanelHead>

        <NavScroll>
          {/* ── INFORMATIONS (device) ── */}
          {sidebarConfig.textSidebar.sections['device-info'] !== false && (
            <>
              <NavBtn $act={is('device-info')} onClick={() => nav('device-info')}>
                <Info size={16} /> Informations
              </NavBtn>
              <NavDiv />
            </>
          )}

          {/* ── INSTAGRAM ── */}
          {sidebarConfig.textSidebar.sections.insta !== false && (<>
          <SectionHead $act={is('accounts-list')} onClick={() => nav('accounts-list')}>
            <Instagram size={16} /> Instagram
            <ChevronToggle onClick={e => { e.stopPropagation(); setInstaOpen(!instaOpen); }}>
              {instaOpen ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
            </ChevronToggle>
          </SectionHead>
          <SubWrap $open={instaOpen}>
            {INSTA_ITEMS.map(it => {
              if (it.id === 'accounts') {
                // "Comptes" — collapsible with account sub-list (always shows chevron)
                return (
                  <React.Fragment key={it.id}>
                    <NavBtn $act={is(it.id)} $sub onClick={() => nav(it.id)} style={{ position: 'relative' }}>
                      <it.icon size={14} /> {it.label}
                      {currentDeviceAccounts.length > 0 && (
                        <span onClick={e => { e.stopPropagation(); setAccountsOpen(!accountsOpen); }}
                          style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 2, padding: '2px 4px', borderRadius: 4, cursor: 'pointer', color: 'var(--text-muted)' }}>
                          <span style={{ fontSize: 10, fontWeight: 700, opacity: .6 }}>{currentDeviceAccounts.length}</span>
                          {accountsOpen ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
                        </span>
                      )}
                    </NavBtn>
                    {accountsOpen && currentDeviceAccounts.length > 0 && (
                      <div style={{ paddingLeft: 18 }}>
                        {currentDeviceAccounts.map(acct => {
                          const isActive = selDev?.account_id === acct.account_id;
                          const uname = acct.instagram_username || `ID ${acct.account_id}`;
                          return (
                            <div
                              key={acct.account_id}
                              onClick={() => switchAccount(acct)}
                              style={{
                                display: 'flex', alignItems: 'center', gap: 8, padding: '6px 10px', borderRadius: 8,
                                cursor: 'pointer', fontSize: 13, fontWeight: isActive ? 700 : 500,
                                color: isActive ? 'var(--primary)' : 'var(--text-secondary)',
                                background: isActive ? 'rgba(99,102,241,.08)' : 'transparent',
                                transition: 'all .12s', marginBottom: 2
                              }}
                              onMouseEnter={e => { if (!isActive) e.currentTarget.style.background = 'var(--surface-hover)'; }}
                              onMouseLeave={e => { if (!isActive) e.currentTarget.style.background = 'transparent'; }}
                            >
                              <div style={{
                                width: 24, height: 24, borderRadius: '50%', flexShrink: 0, overflow: 'hidden',
                                background: 'var(--gradient)', display: 'flex', alignItems: 'center', justifyContent: 'center',
                                color: '#fff', fontSize: 10, fontWeight: 700
                              }}>
                                <img
                                  src={getLocalProfilePicUrl(acct.instagram_username || '')}
                                  alt=""
                                  style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                  onError={e => {
                                    const step = parseInt(e.target.dataset.fallback || '0');
                                    if (step === 0) { e.target.dataset.fallback = '1'; e.target.src = getProfilePicUrl(acct.instagram_username || ''); }
                                    else { e.target.style.display = 'none'; e.target.parentElement.textContent = (uname[0] || '?').toUpperCase(); }
                                  }}
                                />
                              </div>
                              <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1, minWidth: 0 }}>
                                @{uname}
                              </span>
                              <span style={{ fontSize: 9, fontWeight: 700, padding: '1px 5px', borderRadius: 3, background: 'var(--surface)', color: 'var(--text-muted)', flexShrink: 0, whiteSpace: 'nowrap' }}>
                                {acct.app_id === 'com.instagram.android' ? 'IG' : (acct.app_id?.slice(-1).toUpperCase() || '?')}
                              </span>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </React.Fragment>
                );
              }
              return (
                <NavBtn key={it.id} $act={is(it.id)} $sub onClick={() => nav(it.id)}>
                  <it.icon size={14} /> {it.label}
                </NavBtn>
              );
            })}
          </SubWrap>
          <NavDiv />
          </>)}

          {/* ── GMAIL ── */}
          {sidebarConfig.textSidebar.sections.gmail !== false && hasModule('gmail') && (<>
          <SectionHead onClick={() => { nav(SECTION_DEFAULT_TAB.gmail); setGmailOpen(true); }}>
            <Mail size={16} /> Gmail
            <ChevronToggle onClick={e => { e.stopPropagation(); setGmailOpen(!gmailOpen); }}>
              {gmailOpen ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
            </ChevronToggle>
          </SectionHead>
          <SubWrap $open={gmailOpen}>
            {GMAIL_ITEMS.map(it => (
              <NavBtn key={it.id} $act={is(it.id)} $sub onClick={() => nav(it.id)}>
                <it.icon size={14} /> {it.label}
              </NavBtn>
            ))}
          </SubWrap>
          <NavDiv />
          </>)}

          {/* ── TIKTOK ── */}
          {sidebarConfig.textSidebar.sections.tiktok !== false && hasModule('tiktok') && (<>
          <SectionHead onClick={() => { nav(SECTION_DEFAULT_TAB.tiktok); setTiktokOpen(true); }}>
            <Video size={16} /> TikTok
            <ChevronToggle onClick={e => { e.stopPropagation(); setTiktokOpen(!tiktokOpen); }}>
              {tiktokOpen ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
            </ChevronToggle>
          </SectionHead>
          <SubWrap $open={tiktokOpen}>
            {TIKTOK_ITEMS.map(it => (
              <NavBtn key={it.id} $act={is(it.id)} $sub onClick={() => nav(it.id)}>
                <it.icon size={14} /> {it.label}
              </NavBtn>
            ))}
          </SubWrap>
          <NavDiv />
          </>)}

          {/* ── GOOGLE ── */}
          {sidebarConfig.textSidebar.sections.google !== false && hasModule('google') && (<>
          <SectionHead onClick={() => { nav(SECTION_DEFAULT_TAB.google); setGoogleOpen(true); }}>
            <Globe size={16} /> Google
            <ChevronToggle onClick={e => { e.stopPropagation(); setGoogleOpen(!googleOpen); }}>
              {googleOpen ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
            </ChevronToggle>
          </SectionHead>
          <SubWrap $open={googleOpen}>
            {GOOGLE_ITEMS.map(it => (
              <NavBtn key={it.id} $act={is(it.id)} $sub onClick={() => nav(it.id)}>
                <it.icon size={14} /> {it.label}
              </NavBtn>
            ))}
          </SubWrap>
          <NavDiv />
          </>)}

          {/* ── TWITTER/X ── */}
          {sidebarConfig.textSidebar.sections.twitter !== false && hasModule('twitter') && (<>
          <SectionHead onClick={() => { nav(SECTION_DEFAULT_TAB.twitter); setTwitterOpen(true); }}>
            <MessageCircle size={16} /> Twitter/X
            <ChevronToggle onClick={e => { e.stopPropagation(); setTwitterOpen(!twitterOpen); }}>
              {twitterOpen ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
            </ChevronToggle>
          </SectionHead>
          <SubWrap $open={twitterOpen}>
            {TWITTER_ITEMS.map(it => (
              <NavBtn key={it.id} $act={is(it.id)} $sub onClick={() => nav(it.id)}>
                <it.icon size={14} /> {it.label}
              </NavBtn>
            ))}
          </SubWrap>
          <NavDiv />
          </>)}

          {/* ── TINDER ── */}
          {sidebarConfig.textSidebar.sections.tinder !== false && hasModule('tinder') && (<>
          <SectionHead onClick={() => { nav(SECTION_DEFAULT_TAB.tinder); setTinderOpen(true); }}>
            <Heart size={16} /> Tinder
            <ChevronToggle onClick={e => { e.stopPropagation(); setTinderOpen(!tinderOpen); }}>
              {tinderOpen ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
            </ChevronToggle>
          </SectionHead>
          <SubWrap $open={tinderOpen}>
            {TINDER_ITEMS.map(it => (
              <NavBtn key={it.id} $act={is(it.id)} $sub onClick={() => nav(it.id)}>
                <it.icon size={14} /> {it.label}
              </NavBtn>
            ))}
          </SubWrap>
          <NavDiv />
          </>)}

          {/* ── FLEET LEADS ── */}
          {sidebarConfig.textSidebar.sections['fleet-leads'] !== false && (<>
          <NavBtn $act={is('fleet-leads')} onClick={() => nav('fleet-leads')}>
            <TrendingUp size={16} /> Lead Scraper
          </NavBtn>
          <NavDiv />
          </>)}

          {/* ── DONNÉES ── */}
          {sidebarConfig.textSidebar.sections.data !== false && (<>
          <SectionHead onClick={() => setDataOpen(!dataOpen)}>
            <Database size={16} /> Données
            <ChevronToggle onClick={e => { e.stopPropagation(); setDataOpen(!dataOpen); }}>
              {dataOpen ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
            </ChevronToggle>
          </SectionHead>
          <SubWrap $open={dataOpen}>
            {DATA_ITEMS.map(it => (
              <NavBtn key={it.id} $act={is(it.id)} $sub onClick={() => nav(it.id)}>
                <it.icon size={14} /> {it.label}
              </NavBtn>
            ))}
          </SubWrap>
          </>)}

          {/* ── DEVICES (text sidebar, off by default) ── */}
          {sidebarConfig.textSidebar.sections.devices && (
            <>
              <NavDiv />
              <NavBtn $act={is('devices')} onClick={() => nav('devices')}>
                <LayoutGrid size={16} /> Devices
              </NavBtn>
            </>
          )}

          {/* ── CRM (text sidebar, off by default) ── */}
          {sidebarConfig.textSidebar.sections.crm && (
            <NavBtn $act={is('crm')} onClick={() => nav('crm')}>
              <Contact size={16} /> CRM
            </NavBtn>
          )}

          {/* ── CENTRE DE CONTRÔLE (text sidebar, off by default) ── */}
          {sidebarConfig.textSidebar.sections['impulse-bot'] && (
            <NavBtn $act={is('impulse-bot')} onClick={() => nav('impulse-bot')}>
              <LayoutDashboard size={16} /> Centre de Contrôle
            </NavBtn>
          )}

          {/* ── WORKFLOWS (text sidebar, off by default) ── */}
          {sidebarConfig.textSidebar.sections['workflow-builder'] && (
            <NavBtn $act={is('workflow-builder')} onClick={() => nav('workflow-builder')}>
              <Zap size={16} /> Workflows
            </NavBtn>
          )}

          {/* ── SCRIPT FLOWS (text sidebar, off by default) ── */}
          {sidebarConfig.textSidebar.sections['script-flows'] && (
            <NavBtn $act={is('script-flows')} onClick={() => nav('script-flows')}>
              <Layers size={16} /> Script Flows
            </NavBtn>
          )}

        </NavScroll>

        {/* ── Fixed bottom nav (does not scroll) ── */}
        <div style={{ flexShrink: 0, borderTop: '1px solid var(--border)', padding: '6px 0' }}>
          {sidebarConfig.textSidebar.bottomNav['accounts-list'] !== false && (
            <NavBtn $act={is('accounts-list')} onClick={() => nav('accounts-list')}>
              <Instagram size={16} /> Instagram
            </NavBtn>
          )}
          {sidebarConfig.textSidebar.bottomNav['impulse-bot'] !== false && (
            <NavBtn $act={is('impulse-bot')} onClick={() => nav('impulse-bot')}>
              <LayoutDashboard size={16} /> Centre de contrôle
            </NavBtn>
          )}
          {sidebarConfig.textSidebar.bottomNav['workflow-builder'] !== false && (
            <NavBtn $act={is('workflow-builder')} onClick={() => nav('workflow-builder')}>
              <Zap size={16} /> Workflows
            </NavBtn>
          )}
          {sidebarConfig.textSidebar.bottomNav['script-flows'] && (
            <NavBtn $act={is('script-flows')} onClick={() => nav('script-flows')}>
              <Layers size={16} /> Script Flows
            </NavBtn>
          )}
          {sidebarConfig.textSidebar.bottomNav.devices !== false && (
            <NavBtn $act={is('devices')} onClick={() => nav('devices')}>
              <Smartphone size={16} /> Devices
            </NavBtn>
          )}
          {sidebarConfig.textSidebar.bottomNav.crm && (
            <NavBtn $act={is('crm')} onClick={() => nav('crm')}>
              <Contact size={16} /> CRM
            </NavBtn>
          )}
          {sidebarConfig.textSidebar.bottomNav.gmail && (
            <NavBtn $act={is('gmail-auto')} onClick={() => nav('gmail-auto')}>
              <Mail size={16} /> Gmail
            </NavBtn>
          )}
          {sidebarConfig.textSidebar.bottomNav.tiktok && (
            <NavBtn $act={is('tiktok-auto')} onClick={() => nav('tiktok-auto')}>
              <Video size={16} /> TikTok
            </NavBtn>
          )}
          {sidebarConfig.textSidebar.bottomNav.google && (
            <NavBtn $act={is('google-auto')} onClick={() => nav('google-auto')}>
              <Globe size={16} /> Google
            </NavBtn>
          )}
          {sidebarConfig.textSidebar.bottomNav.twitter && (
            <NavBtn $act={is('twitter-auto')} onClick={() => nav('twitter-auto')}>
              <MessageCircle size={16} /> Twitter/X
            </NavBtn>
          )}
          {sidebarConfig.textSidebar.bottomNav.tinder && (
            <NavBtn $act={is('tinder-auto') || is('tinder-account')} onClick={() => nav('tinder-auto')}>
              <Heart size={16} /> Tinder
            </NavBtn>
          )}
          {sidebarConfig.textSidebar.bottomNav.data && (
            <NavBtn $act={is('database')} onClick={() => nav('database')}>
              <Database size={16} /> Données
            </NavBtn>
          )}
          {sidebarConfig.textSidebar.bottomNav['device-info'] && (
            <NavBtn $act={is('device-info')} onClick={() => nav('device-info')}>
              <Info size={16} /> Informations
            </NavBtn>
          )}
          {sidebarConfig.textSidebar.bottomNav.settings && (
            <NavBtn $act={is('settings')} onClick={() => nav('settings')}>
              <Settings size={16} /> Paramètres
            </NavBtn>
          )}
        </div>

      </ExtPanel>
      {/* Context menu */}
      {ctxMenu && (
        <ContextMenu ref={ctxRef} style={{ left: ctxMenu.x, top: ctxMenu.y }}>
          <CtxItem onClick={startRename}><Pencil size={13} /> Renommer</CtxItem>
          {ctxMenu.type === 'group' && (<>
            <div style={{ padding: '6px 12px', display: 'flex', gap: 4, flexWrap: 'wrap' }}>
              {['#a591ef','#ef4444','#22c55e','#f59e0b','#3b82f6','#ec4899','#06b6d4','#8b5cf6','#f97316','#6366f1'].map(c => (
                <div key={c} onClick={() => changeGroupColor(ctxMenu.id, c)}
                  style={{ width: 18, height: 18, borderRadius: '50%', background: c, cursor: 'pointer',
                    border: '2px solid transparent', transition: 'all .1s' }}
                  onMouseEnter={e => e.target.style.border = '2px solid #fff'}
                  onMouseLeave={e => e.target.style.border = '2px solid transparent'} />
              ))}
            </div>
            <CtxItem $danger onClick={deleteGroup}><Trash2 size={13} /> Supprimer le groupe</CtxItem>
          </>)}
        </ContextMenu>
      )}
    </SidebarWrapper>

      {/* Logout confirmation dialog */}
      {showLogoutConfirm && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 99999, background: 'rgba(0,0,0,.55)', display: 'flex', alignItems: 'center', justifyContent: 'center' }} onClick={() => setShowLogoutConfirm(false)}>
          <div style={{ background: 'var(--surface, #1e1e2e)', border: '1px solid var(--border, #333)', borderRadius: 12, padding: '28px 32px', minWidth: 320, textAlign: 'center', boxShadow: '0 8px 32px rgba(0,0,0,.4)' }} onClick={e => e.stopPropagation()}>
            <LogOut size={28} style={{ color: 'var(--danger, #ef4444)', marginBottom: 12 }} />
            <h3 style={{ margin: '0 0 8px', color: 'var(--text, #fff)', fontSize: 16 }}>Déconnexion</h3>
            <p style={{ margin: '0 0 20px', color: 'var(--text-muted, #888)', fontSize: 13 }}>Voulez-vous vraiment vous déconnecter ?</p>
            <div style={{ display: 'flex', gap: 10, justifyContent: 'center' }}>
              <button onClick={() => setShowLogoutConfirm(false)} style={{ padding: '8px 20px', borderRadius: 8, border: '1px solid var(--border, #333)', background: 'transparent', color: 'var(--text, #fff)', cursor: 'pointer', fontSize: 13 }}>Annuler</button>
              <button onClick={() => { setShowLogoutConfirm(false); logout(); }} style={{ padding: '8px 20px', borderRadius: 8, border: 'none', background: 'var(--danger, #ef4444)', color: '#fff', cursor: 'pointer', fontSize: 13, fontWeight: 600 }}>Déconnecter</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Sidebar;
